/**
 * ComparePanel - Main comparison panel in adaptive sidebar
 * 
 * Features:
 * - Vertical cell chips (CompareCellChip components)
 * - View mode selector (Table/Charts/2D/3D)
 * - Inline comparison views
 * - Back to Filters button
 * - Clear All button
 */

import { h } from 'preact'
import { useState, useEffect } from 'preact/hooks'
import { useLocation } from 'wouter-preact'
import { compareCells, removeFromCompare, clearCompare, panelMode, addToCompare } from '../../state/filterStore.js'
import { layoutMode, allCells } from '../../state/globalSignals.js'
import { appViewMode } from '../../state/appViewMode.js'
import { consumeCompareDeepDive } from '../../services/demoLimitService.js'
import CompareCellChip from './CompareCellChip.jsx'
import { IconSearch, IconX, IconArrowRight, IconPlus } from '../shared/Icons.jsx'

export default function ComparePanel() {
  const [, setLocation] = useLocation()
  const cellIds = compareCells.value
  const cellCount = cellIds.length
  const [searchQuery, setSearchQuery] = useState('')
  const [searchResults, setSearchResults] = useState([])
  const [showSearch, setShowSearch] = useState(false)
  
  // Use allCells from global state (already loaded in MainAppPage)
  const cells = allCells.value
  
  // Filter cells based on search query
  useEffect(() => {
    if (!searchQuery.trim()) {
      setSearchResults([])
      return
    }
    
    const query = searchQuery.toLowerCase()
    
    const filtered = cells
      .filter(cell => {
        // Search in manufacturer and name fields - they're at the top level
        const manufacturer = (cell.manufacturer || '').toLowerCase()
        const name = (cell.name || '').toLowerCase()
        const searchText = `${manufacturer} ${name}`
        return searchText.includes(query)
      })
      .filter(cell => !cellIds.includes(cell.id)) // Use cell.id not cell.product_id
      .slice(0, 5) // Limit to 5 results
    
    setSearchResults(filtered)
  }, [searchQuery, cells, cellIds])
  
  const handleAddCell = async (cellId) => {
    if (cellCount >= 5) {
      alert('Maximum 5 cells can be compared')
      return
    }

    // Capture the cell's display info before the search UI clears, so the
    // limit-overlay chip has a name/manufacturer even after the dropdown closes.
    const cellObj = cells.find((c) => c.id === cellId) || null

    // Clear the search box immediately for a responsive feel.
    setSearchQuery('')
    setSearchResults([])

    // Shared eager-load-or-block gate (same path as the grid/table "+" icon):
    // for DEMO it deep-dives the cell NOW so the meter increments live and the
    // cell is recorded viewed-today; at the limit it returns false (overlay
    // surfaced, no phantom chip). Non-demo → true immediately (no quota).
    const ok = await consumeCompareDeepDive({
      id: cellId,
      name: cellObj?.name,
      manufacturer: cellObj?.manufacturer,
    })
    if (ok) addToCompare(cellId)
  }
  
  const handleBackToFilters = () => {
    panelMode.value = 'filter'
  }
  
  const handleViewComparison = () => {
    console.log('[ComparePanel] Navigating to /compare')
    // Save current specific view (e.g., 'cells-grid', 'cells-table', 'cells-ragone', 'benchmarking-score')
    const currentView = appViewMode.value
    localStorage.setItem('previousCellsView', currentView)
    console.log('[ComparePanel] Saved previous view:', currentView)
    // Navigate to Compare main tab - router will set layoutMode
    setLocation('/compare')
  }
  
  const handleClearAll = () => {
    clearCompare()
  }
  
  return (
    <div className="compare-panel" data-tour="benchmarking-compare-sidebar">
      {/* Header */}
      <div className="compare-panel-header">
        <div className="compare-header-left">
          <h2 className="compare-title">
            Compare
            <span className="compare-count">({cellCount}/5)</span>
          </h2>
        </div>
      </div>
      
      {/* Quick Add Search (only show if less than 5 cells) */}
      {cellCount < 5 && (
        <div className="compare-quick-add">
          <div className="quick-add-search">
            <IconSearch size={14} />
            <input
              type="text"
              placeholder="Quick add cell..."
              value={searchQuery}
              onInput={(e) => setSearchQuery(e.currentTarget.value)}
              onFocus={() => setShowSearch(true)}
              className="quick-add-input"
              autoComplete="off"
              spellCheck="false"
            />
            {searchQuery && (
              <button
                className="quick-add-clear"
                onClick={() => {
                  setSearchQuery('')
                  setSearchResults([])
                }}
                title="Clear search"
              >
                <IconX size={12} />
              </button>
            )}
          </div>
          
          {/* Search Results Dropdown */}
          {searchQuery.trim() && (
            <div className="quick-add-results">
              {searchResults.length > 0 ? (
                searchResults.map(cell => {
                  const manufacturer = cell.manufacturer || 'Unknown'
                  const name = cell.name || 'Unknown'
                  
                  return (
                    <button
                      key={cell.id}
                      className="quick-add-result"
                      onClick={() => handleAddCell(cell.id)}
                      title={`Add ${manufacturer} ${name}`}
                    >
                      <IconPlus size={14} />
                      <div className="result-info">
                        <span className="result-name">{manufacturer}</span>
                        <span className="result-model">{name}</span>
                      </div>
                    </button>
                  )
                })
              ) : (
                <div className="quick-add-no-results">
                  No cells found matching "{searchQuery}"
                </div>
              )}
            </div>
          )}
        </div>
      )}
      
      {/* Cell Chips List */}
      <div className="compare-cell-list">
        {cellIds.length === 0 ? (
          <div className="compare-empty-state">
            <div className="empty-state-icon">📊</div>
            <p className="empty-state-text">
              No cells selected for comparison yet
            </p>
            <p className="empty-state-hint">
              Add cells from grid, table, or plots
            </p>
          </div>
        ) : (
          <>
            {cellIds.map((cellId, index) => (
              <CompareCellChip
                key={cellId}
                cellId={cellId}
                index={index}
                onRemove={() => removeFromCompare(cellId)}
              />
            ))}
            
            {/* Clear All Button */}
            {cellCount > 0 && (
              <button 
                className="compare-clear-all"
                onClick={handleClearAll}
                title={`Remove all ${cellCount} cells`}
              >
                <IconX size={16} />
                <span>Clear All</span>
              </button>
            )}
          </>
        )}
      </div>
      
      {/* View Comparison Button (only show if cells exist) */}
      {cellCount >= 2 && (
        <div className="compare-action-section">
          <button 
            className="compare-view-btn"
            onClick={handleViewComparison}
            title="Open detailed comparison view"
          >
            <IconArrowRight size={18} />
            <span>Compare Cells</span>
          </button>
          <p className="compare-hint">Opens detailed comparison in main view</p>
        </div>
      )}
    </div>
  )
}
